import React, { useState, useEffect } from 'react';
import { 
  BookOpen, 
  Search, 
  Plus, 
  Filter, 
  Star, 
  Edit2, 
  Trash2, 
  Eye,
  Save,
  X,
  Tag,
  AlertCircle
} from 'lucide-react';
import { promptService } from '../../services/promptService';

export default function MasterPromptBoard() {
  const [prompts, setPrompts] = useState([]);
  const [northStarPrinciples, setNorthStarPrinciples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedPromptId, setExpandedPromptId] = useState(null);

  useEffect(() => {
    loadData();
    initializeSystemRequirements();
  }, [selectedCategory]);

  const initializeSystemRequirements = async () => {
    try {
      await promptService?.initializePersistentNavigation();
    } catch (err) {
      console.error('Error initializing system requirements:', err);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [promptsData, northStarData, categoriesData] = await Promise.all([
        promptService?.getAll(selectedCategory === 'all' ? null : selectedCategory),
        promptService?.getNorthStarPrinciples(),
        promptService?.getCategories()
      ]);
      
      setPrompts(promptsData);
      setNorthStarPrinciples(northStarData);
      setCategories(categoriesData);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePrompt = async (promptData) => {
    try {
      if (editingPrompt?.id) {
        await promptService?.update(editingPrompt?.id, promptData);
      } else {
        await promptService?.create(promptData);
      }
      
      setIsEditing(false);
      setEditingPrompt(null);
      setShowAddModal(false);
      loadData();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleDeletePrompt = async (id) => {
    if (!confirm('Are you sure you want to delete this prompt?')) return;
    
    try {
      await promptService?.delete(id);
      loadData();
    } catch (err) {
      setError(err?.message);
    }
  };

  const filteredPrompts = prompts?.filter(prompt =>
    prompt?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    prompt?.content?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    prompt?.tags?.some(tag => tag?.toLowerCase()?.includes(searchQuery?.toLowerCase()))
  );

  const getCategoryBadgeColor = (category) => {
    const colors = {
      'system_principles': 'bg-blue-100 text-blue-800',
      'validation_rules': 'bg-purple-100 text-purple-800',
      'interaction_guidelines': 'bg-green-100 text-green-800',
      'north_star_principles': 'bg-yellow-100 text-yellow-800',
      'error_handling': 'bg-red-100 text-red-800',
      'security_policies': 'bg-indigo-100 text-indigo-800'
    };
    return colors?.[category] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <BookOpen className="w-8 h-8 text-indigo-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Master Prompt Board</h1>
                <p className="text-sm text-gray-500 mt-1">AI Knowledge Base & North Star Principles</p>
              </div>
            </div>
            <button
              onClick={() => {
                setEditingPrompt(null);
                setShowAddModal(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Prompt
            </button>
          </div>

          {/* Search and Filter Bar */}
          <div className="mt-6 flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search prompts by title, content, or tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e?.target?.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">All Categories</option>
                {categories?.map(cat => (
                  <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
        )}

        {/* North Star Principles Panel */}
        {northStarPrinciples?.length > 0 && (
          <div className="mb-8 bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-200 rounded-lg p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Star className="w-6 h-6 text-yellow-600 fill-current" />
              <h2 className="text-xl font-bold text-gray-900">North Star Principles</h2>
            </div>
            <div className="space-y-3">
              {northStarPrinciples?.map(principle => (
                <div key={principle?.id} className="bg-white rounded-lg p-4 border border-yellow-200">
                  <h3 className="font-semibold text-gray-900 mb-2">{principle?.title}</h3>
                  <p className="text-gray-700 text-sm">{principle?.content}</p>
                  {principle?.tags && principle?.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {principle?.tags?.map((tag, idx) => (
                        <span key={idx} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <Tag className="w-3 h-3 mr-1" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Prompts Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : filteredPrompts?.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No prompts found</h3>
            <p className="text-gray-500">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {filteredPrompts?.map(prompt => (
              <div
                key={prompt?.id}
                className="bg-white rounded-lg border border-gray-200 hover:border-indigo-300 transition-colors"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{prompt?.title}</h3>
                        {prompt?.isNorthStar && (
                          <Star className="w-5 h-5 text-yellow-500 fill-current" />
                        )}
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryBadgeColor(prompt?.category)}`}>
                          {categories?.find(c => c?.value === prompt?.category)?.label || prompt?.category}
                        </span>
                      </div>
                      {prompt?.description && (
                        <p className="text-sm text-gray-600 mb-3">{prompt?.description}</p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => setExpandedPromptId(expandedPromptId === prompt?.id ? null : prompt?.id)}
                        className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          setEditingPrompt(prompt);
                          setShowAddModal(true);
                        }}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeletePrompt(prompt?.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {expandedPromptId === prompt?.id && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <div className="prose max-w-none">
                        <div className="text-sm text-gray-700 whitespace-pre-wrap">{prompt?.content}</div>
                      </div>
                      {prompt?.tags && prompt?.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-4">
                          {prompt?.tags?.map((tag, idx) => (
                            <span key={idx} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                              <Tag className="w-3 h-3 mr-1" />
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
                        <span>Version {prompt?.version}</span>
                        <span>Priority: {prompt?.priority}</span>
                        <span>Updated {new Date(prompt.updatedAt)?.toLocaleDateString()}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Add/Edit Modal */}
      {showAddModal && (
        <PromptModal
          prompt={editingPrompt}
          categories={categories}
          onSave={handleSavePrompt}
          onClose={() => {
            setShowAddModal(false);
            setEditingPrompt(null);
          }}
        />
      )}
    </div>
  );
}

function PromptModal({ prompt, categories, onSave, onClose }) {
  const [formData, setFormData] = useState({
    title: prompt?.title || '',
    category: prompt?.category || 'system_principles',
    content: prompt?.content || '',
    description: prompt?.description || '',
    isNorthStar: prompt?.isNorthStar || false,
    priority: prompt?.priority || 0,
    tags: prompt?.tags?.join(', ') || '',
    status: prompt?.status || 'active'
  });

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave({
      ...formData,
      tags: formData?.tags?.split(',')?.map(t => t?.trim())?.filter(t => t)
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">
              {prompt ? 'Edit Prompt' : 'Add New Prompt'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              required
              value={formData?.title}
              onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={formData?.category}
              onChange={(e) => setFormData({ ...formData, category: e?.target?.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {categories?.map(cat => (
                <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={formData?.description}
              onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
            <textarea
              required
              rows={8}
              value={formData?.content}
              onChange={(e) => setFormData({ ...formData, content: e?.target?.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <input
                type="number"
                value={formData?.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e?.target?.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formData?.status}
                onChange={(e) => setFormData({ ...formData, status: e?.target?.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
                <option value="under_review">Under Review</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
            <input
              type="text"
              value={formData?.tags}
              onChange={(e) => setFormData({ ...formData, tags: e?.target?.value })}
              placeholder="e.g., core, validation, security"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isNorthStar"
              checked={formData?.isNorthStar}
              onChange={(e) => setFormData({ ...formData, isNorthStar: e?.target?.checked })}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="isNorthStar" className="ml-2 block text-sm text-gray-900">
              Mark as North Star Principle
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Prompt
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}